/**
 * React Query hooks for agent data management
 *
 * Provides hooks for fetching and mutating agent data with
 * automatic cache invalidation and optimistic updates.
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { agentService } from '../services/api';
import {
  Agent,
  AgentCreate,
  AgentUpdate,
  AgentListResponse,
  AgentFilters,
} from '../types/agent';

const AGENTS_QUERY_KEY = 'agents';

/**
 * Hook to fetch list of agents with filters
 */
export function useAgents(filters?: AgentFilters): UseQueryResult<AgentListResponse, Error> {
  return useQuery({
    queryKey: [AGENTS_QUERY_KEY, filters],
    queryFn: () => agentService.list(filters),
    // Keep data fresh for 1 minute
    staleTime: 60000,
  });
}

/**
 * Hook to fetch a single agent by ID
 */
export function useAgent(id: string | undefined): UseQueryResult<Agent, Error> {
  return useQuery({
    queryKey: [AGENTS_QUERY_KEY, id],
    queryFn: () => agentService.get(id!),
    enabled: !!id, // Only run query if id is provided
    staleTime: 60000,
  });
}

/**
 * Hook to create a new agent
 */
export function useCreateAgent(): UseMutationResult<Agent, Error, AgentCreate> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AgentCreate) => agentService.create(data),
    onSuccess: () => {
      // Invalidate and refetch agents list
      queryClient.invalidateQueries({ queryKey: [AGENTS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update an existing agent
 */
export function useUpdateAgent(): UseMutationResult<Agent, Error, { id: string; data: AgentUpdate }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AgentUpdate }) =>
      agentService.update(id, data),
    onSuccess: (updatedAgent) => {
      // Invalidate agents list
      queryClient.invalidateQueries({ queryKey: [AGENTS_QUERY_KEY] });

      // Update the cache for the specific agent
      queryClient.setQueryData(
        [AGENTS_QUERY_KEY, updatedAgent.agent_id],
        updatedAgent
      );
    },
  });
}

/**
 * Hook to delete an agent
 */
export function useDeleteAgent(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentService.delete(id),
    onSuccess: () => {
      // Invalidate and refetch agents list
      queryClient.invalidateQueries({ queryKey: [AGENTS_QUERY_KEY] });
    },
  });
}
